import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { BookingListStoreModule } from '@common/store/booking-list/booking-list.module';
import { CreateSkLkrlStoreModule } from '@common/store/create-sk-lkrl';
import { getMealStorageSync, GetMealStoreModule } from '@common/store/get-meal';
import { GetOrderStoreModule } from '@common/store/get-order';
import { GetOrderListStoreModule } from '@common/store/get-order-list';
import { myBookingStorageSync, MyBookingStoreModule } from '@common/store/my-booking';
import { DATADOG_CONFIG, I18N_CONFIG, LOAD_MASTER_LIST } from '@conf';
import {
  apiAmcmemberFactory,
  apiAuthorizationFactory,
  apiInitializationFactory,
  apiSysdateFactory,
  apiUserFactory,
  apiSearchFactory,
  apiExchangeFactory,
  apiMemberFactory,
  apiCreditFactory,
} from '@conf/api.config';
import { environment } from '@env/environment';
import {
  AlertAreaModule,
  AnaBizHeaderModule,
  BasicPageLayoutModule,
  ContentsAreaModule,
  DebugAreaModule,
  EmergencyAreaModule,
  ErrorGuidanceContModule,
  FooterModule,
  GuidanceAreaModule,
  HeaderModule,
  ModalModule,
  NotificationAreaModule,
  PageLoadingModule,
} from '@lib/components';
import { ITranslationResource, MultiTranslateHttpLoader } from '@lib/core';
import { ApiInterceptor, CacheInterceptor } from '@lib/interceptor';
import {
  AswMasterService,
  AswMissingTranslationHandler,
  InitialInfluxService,
  InitialInfluxServiceModule,
  LoggerDatadogServiceModule,
  TealiumService,
  RESTRICTED_NAVIGATION_INITIALIZER_PROVIDER,
  LoggerDatadogService,
} from '@lib/services';
import {
  AMCMemberStoreModule,
  AnaBizContextStoreModule,
  ApiErrorResponseStoreModule,
  AswCommonStoreModule,
  AswContextStoreModule,
  AswMasterStoreModule,
  AswServiceStoreModule,
  DynamicContentStoreModule,
  NotRetryableErrorStoreModule,
  PageLoadingStoreModule,
  RetryableErrorStoreModule,
  SysdateStoreModule,
  aswServiceStorageSync,
  storeSyncMetaReducer,
  GetMemberInformationStoreModule,
} from '@lib/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule, routerReducer } from '@ngrx/router-store';
import { Action, ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateModuleConfig,
} from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ApiAmcmemberModule } from 'src/sdk-amcmember';
import { ApiAuthorizationModule } from 'src/sdk-authorization';
import { ApiInitializationModule } from 'src/sdk-initialization';
import { ApiSysdateModule } from 'src/sdk-sysdate';
import { ApiUserModule } from 'src/sdk-user';
import { ApiExchangeModule } from 'src/sdk-exchange';
import { ApiMemberModule } from 'src/sdk-member';
import { ApiCreditModule } from 'src/sdk-credit';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HelloWorldContModule } from './hello-world';
import { MyBookingSubHeaderModule } from './sub-header/my-booking/my-booking-sub-header.module';
import { SampleSubHeaderModule } from './sub-header/sample-sub-header/sample-sub-header.module';
import { TestModule } from './test';
import { TestSsnModule } from './test-ssn/test-ssn.module';
import { RESTRICTED_NAVIGATION_SERVICE_CONFIG_PROVIDER } from '@conf';
import { ApiModule as ApiServicingModule } from 'src/sdk-servicing';
import { ApiModule as ApiSearchModule } from 'src/sdk-search';
import { apiServicingFactory } from '@conf/api.config';
import { ServicingCommonStoreModule, servicingCommonStorageSync } from '@common/store/servicing-common';
import { ServicingSeatmapStoreModule, servicingSeatmapStorageSync } from '@common/store/servicing-seatmap';
import { ServicingRouteReuseStrategy } from '@common/strategy/servicing-route-reuse-strategy';
import { YamatoBaggageGuidanceSubHeaderModule } from './sub-header/yamato-baggage-guidance-sub-header';
import { EMDSearchSubHeaderModule } from './sub-header/emd-search-sub-header/emd-search-sub-header.module';
import { ReceiptSelectSubHeaderModule } from './sub-header/receipt-select-sub-header/receipt-select-sub-header.module';
import { UpdateTravelersStoreModule } from '@common/store/update-travelers';
import { getFareConditionsStorageSync, GetFareConditionsStoreModule } from '@common/store/get-fare-conditions';
import { GetReceiptJuniorPilotStoreModule } from '@common/store/get-receipt-junior-pilot';
import { BookingListSubHeaderModule } from './sub-header/booking-list/booking-list-sub-header.module';
import { PassengerInformationInputModalStoreModule } from '@common/store/my-booking/passenger-information-input-modal';
import { DutyFreePreorderGuidanceSubHeaderModule } from './sub-header/duty-free-preorder-guidance-sub-header';
import { ETicketEmdSelectSubHeaderModule } from './sub-header/e-ticket-emd-select-sub-header';
import { AnaBizStatementOfChargesSelectSubHeaderModule } from './sub-header/ana-biz-statement-of-charges-select-sub-header/ana-biz-statement-of-charges-select-sub-header.module';
import { ReceiptSearchSubHeaderModule } from './sub-header/receipt-search-sub-header';
import { ReservationBasicInformationInputSubHeaderModule } from './sub-header/reservation-basic-information-input-sub-header';
import { ServiceRequestCompletedSubHeaderModule } from './sub-header/service-request-completed/service-request-completed-sub-header.module';
import { ServiceRequestCompletedStoreModule } from '@common/store/service-request-completed/service-request-completed.module';
import { BookingSearchSubHeaderModule } from './sub-header/booking-search/booking-search-sub-header.module';
import { BookingSearchStoreModule, SearchDecryptionStoreModule } from '@common/store';
import { UpdateServicesStoreModule } from '@common/store/update-services';
import { ServiceRequestPaymentInputStoreModule } from '@common/store/service-request-payment-input/service-request-payment-input.module';
import {
  // member
  ExchangeAvailabilityStoreModule,
  GetRequiredMilesStoreModule,
  MemberAvailabilityStoreModule,
  ReservationAvailabilityStoreModule,
  // credit
  GetCreditPanInformationStoreModule,
} from '@common/store';
import { MyBookingContModule } from './my-booking/container/my-booking-cont.module';

import { ServiceRequestPaymentInputSubHeaderModule } from './sub-header/service-request-payment-input-sub-header/service-request-payment-input-sub-header.module';
import { ServiceRequestPaymentInputContModule } from './service-request-payment-input/container/service-request-payment-input-cont.module';

import { AnaBizApplicationListSubHeaderModule } from './sub-header/ana-biz-application-list-sub-header';
import { MyCarValetRequestSubHeaderModule } from './sub-header/my-car-valet-request-sub-header';
import { JuniorPilotRequestSubHeaderModule } from './sub-header/junior-pilot-request-sub-header';
import { PetEasyTravelRequestSubHeaderModule } from './sub-header/pet-easy-travel-request-sub-header';
import { InformativeSeatmapSubHeaderModule } from './sub-header/informative-seatmap-sub-header';
import { PassAmcInformationStoreServiceModule } from '@common/services/pass-amc-information-store/pass-amc-information.module';
import {
  servicingSeatmapBackupStorageSync,
  ServicingSeatmapBackupStoreModule,
} from '@common/store/servicing-seatmap-backup';
import { BoardingCertificateSearchSubHeaderModule } from './sub-header/boarding-certificate-search-sub-header';
import { BROWSER_NAVIGATION_INITIALIZER_PROVIDER } from '@common/services/browser-navigation/browser-navigation.service';

/** `MultiTranslateHttpLoader`用のFactory */
export const MultiTranslateHttpLoaderFactory = (httpClient: HttpClient) => {
  // i18n（国際化対応）Configに定義されたファイルリストをもとにLoader用のFactoryを生成
  const {
    baseUrl,
    app: { cache },
  } = environment.spa;
  const resources = I18N_CONFIG.loadFileList.reduce((previous, current) => {
    if (current.suffix) {
      previous.push({
        prefix: `${baseUrl}${cache}/${current.prefix}`,
        suffix: current.suffix,
      });
    } else {
      previous.push({ prefix: `${baseUrl}${cache}/${current.prefix}` });
    }
    return previous;
  }, [] as ITranslationResource[]);
  return new MultiTranslateHttpLoader(httpClient, resources);
};

/** `TranslateModule.forRoot`用Config */
export const ROOT_NGX_TRANSLATE_MODULE_CONFIG: TranslateModuleConfig = {
  loader: {
    provide: TranslateLoader,
    useFactory: MultiTranslateHttpLoaderFactory,
    deps: [HttpClient],
  },
  missingTranslationHandler: {
    provide: MissingTranslationHandler,
    useClass: AswMissingTranslationHandler,
  },
};

/** `StoreModule.forRoot`用routerReducer */
const rootReducers = {
  router: routerReducer,
};
/** `StoreModule.forRoot`用sessionStorage同期指定  */
const metaReducers = [storageSyncWrapper];
export function storageSyncWrapper<T, V extends Action = Action>(reducer: ActionReducer<T, V>): ActionReducer<T, V> {
  // storeとsessionStorageの同期指定
  const storageStates: any = [
    // 共通Storeの中でサービス共通のみセッション永続化の対象であるため追加
    { aswService: aswServiceStorageSync },
    // サービシングでセッション永続化の対象であるサービシング共通を追加
    { servicingCommon: servicingCommonStorageSync },
    { servicingSeatmap: servicingSeatmapStorageSync },
    { myBooking: myBookingStorageSync },
    { getMeal: getMealStorageSync },
    { getFareConditions: getFareConditionsStorageSync },
    { servicingSeatmapBackup: servicingSeatmapBackupStorageSync },
  ];
  return storeSyncMetaReducer({ keys: storageStates, rehydrate: true, storage: sessionStorage || localStorage })(
    reducer
  );
}

/** デフォルトの初期化処理（一番最初に実行する処理） */
function defaultInitializer(tealiumSvc: TealiumService, loggerSvc: LoggerDatadogService) {
  return async () => {
    // Datadogブラウザログの初期化
    loggerSvc.init();
    // Tealium連携用Global変数の初期化
    tealiumSvc.init();
    // Tealium用JavaScriptロード
    return lastValueFrom(tealiumSvc.loadScript$());
  };
}

// 初期流入処理
function initialize(service: InitialInfluxService, masterService: AswMasterService) {
  return async () => {
    await service.exe();
    await lastValueFrom(masterService.load(LOAD_MASTER_LIST));
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(ROOT_NGX_TRANSLATE_MODULE_CONFIG),
    StoreModule.forRoot(rootReducers, { metaReducers }),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.envName !== 'prd',
    }),
    StoreRouterConnectingModule.forRoot(),
    // [start] テンプレート用サンプルcomponent
    HelloWorldContModule,
    TestModule,
    TestSsnModule,
    SampleSubHeaderModule,
    BookingListSubHeaderModule,
    MyBookingContModule,
    ServiceRequestPaymentInputContModule,
    // [end] テンプレート用サンプルcomponent
    BasicPageLayoutModule,
    ContentsAreaModule,
    EmergencyAreaModule,
    HeaderModule,
    AnaBizHeaderModule,
    AlertAreaModule,
    GuidanceAreaModule,
    FooterModule,
    DebugAreaModule,
    NotificationAreaModule,
    ModalModule,
    InitialInfluxServiceModule,
    ErrorGuidanceContModule,
    PageLoadingModule,
    // [start] 共通ストア系
    SysdateStoreModule,
    AswServiceStoreModule,
    AswContextStoreModule,
    AswCommonStoreModule,
    AnaBizContextStoreModule,
    AMCMemberStoreModule,
    AswMasterStoreModule,
    ApiErrorResponseStoreModule,
    RetryableErrorStoreModule,
    NotRetryableErrorStoreModule,
    DynamicContentStoreModule,
    PageLoadingStoreModule,
    ServicingCommonStoreModule,
    ServicingSeatmapStoreModule,
    MyBookingStoreModule,
    PassengerInformationInputModalStoreModule,
    GetOrderStoreModule,
    GetFareConditionsStoreModule,
    GetMealStoreModule,
    GetOrderListStoreModule,
    SearchDecryptionStoreModule,
    UpdateServicesStoreModule,
    BookingListStoreModule,
    BookingSearchStoreModule,
    CreateSkLkrlStoreModule,
    GetReceiptJuniorPilotStoreModule,
    UpdateTravelersStoreModule,
    ServiceRequestPaymentInputStoreModule,
    ServiceRequestCompletedStoreModule,
    PassAmcInformationStoreServiceModule,
    ServicingSeatmapBackupStoreModule,
    // member
    ExchangeAvailabilityStoreModule,
    GetMemberInformationStoreModule,
    GetRequiredMilesStoreModule,
    MemberAvailabilityStoreModule,
    ReservationAvailabilityStoreModule,
    // credit
    GetCreditPanInformationStoreModule,
    // [end] 共通ストア系
    LoggerDatadogServiceModule.forRoot(() => DATADOG_CONFIG),
    // [start] ApiModule
    ApiAmcmemberModule.forRoot(apiAmcmemberFactory),
    ApiAuthorizationModule.forRoot(apiAuthorizationFactory),
    ApiInitializationModule.forRoot(apiInitializationFactory),
    ApiUserModule.forRoot(apiUserFactory),
    ApiSysdateModule.forRoot(apiSysdateFactory),
    ApiServicingModule.forRoot(apiServicingFactory),
    ApiSearchModule.forRoot(apiSearchFactory),
    ApiExchangeModule.forRoot(apiExchangeFactory),
    ApiMemberModule.forRoot(apiMemberFactory),
    ApiCreditModule.forRoot(apiCreditFactory),
    // [end] ApiModule
    // [start] sub-header Module
    YamatoBaggageGuidanceSubHeaderModule,
    EMDSearchSubHeaderModule,
    ReceiptSelectSubHeaderModule,
    DutyFreePreorderGuidanceSubHeaderModule,
    ETicketEmdSelectSubHeaderModule,
    AnaBizStatementOfChargesSelectSubHeaderModule,
    ReceiptSearchSubHeaderModule,
    ReservationBasicInformationInputSubHeaderModule,
    MyBookingSubHeaderModule,
    BookingSearchSubHeaderModule,
    ServiceRequestPaymentInputSubHeaderModule,
    ServiceRequestCompletedSubHeaderModule,
    AnaBizApplicationListSubHeaderModule,
    MyCarValetRequestSubHeaderModule,
    JuniorPilotRequestSubHeaderModule,
    PetEasyTravelRequestSubHeaderModule,
    InformativeSeatmapSubHeaderModule,
    BoardingCertificateSearchSubHeaderModule,
    // [end] sub-header Module
  ],
  providers: [
    // API接続Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    // Akamai Cache接続Interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    // デフォルトの初期化処理
    {
      provide: APP_INITIALIZER,
      useFactory: defaultInitializer,
      deps: [TealiumService, LoggerDatadogService],
      multi: true,
    },
    // 初期流入
    {
      provide: APP_INITIALIZER,
      useFactory: initialize,
      deps: [InitialInfluxService, AswMasterService, Router],
      multi: true,
    },
    // G03_共通部品_共通・汎用処理（520_ブラウザバック・直リンク・F5制御機能）
    RESTRICTED_NAVIGATION_SERVICE_CONFIG_PROVIDER,
    RESTRICTED_NAVIGATION_INITIALIZER_PROVIDER,
    BROWSER_NAVIGATION_INITIALIZER_PROVIDER,
    // コンポーネントの再利用処理を定義する
    {
      provide: RouteReuseStrategy,
      useClass: ServicingRouteReuseStrategy,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
